import React, {useCallback, useMemo} from 'react';
import {ButtonBase, CircularProgress, Divider, Paper, Stack, Switch, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';

import {StyledTable} from '../../../@extended/CustomTables';
import TableBodyCell from '../../../@extended/TableBodyCell';
import {ExportToolBarWithSearch} from '../../../@extended/DataGridTables';
import {sortNumbers, sortStrings} from '../../../../utils/sorting';
import {ReportsColumns, valueGetter} from '../../../../constants/tableColumns';
import {useSubscribeReportMutation} from '../../../../../features/loyaltyCloudApi/apiSlice';
import {
    useSendAllTimeCashBackTransactionsReportMutation,
    useSendClaimedCashBackReportMutation,
    useSendPropertySummaryReportMutation
} from '../../../../../features/coreApi/apiSlice';
import {getAccountSfid, getEmail, getTeamMemberId, getTeamMemberReportSubscriptionList} from '../../../../../app/store/reducers/session';

const AllTimeCashBackTransactionsReportProps = {
    report_id: 'all_time_cash_back_transaction_report',
    report_name: 'All time Cash Back transaction export'
};

const ClaimedCashBackReportProps = {report_id: 'claimed_cash_back_report', report_name: 'Claimed Cash Back export'};

const ClaimedCashBackSummaryReport = {report_id: 'property_summary_report', report_name: 'Claimed Cash Back summary report'};

const WeeklySubCell = ({isSubscribedWeekly, SubscribedWeekly}) => {
    return (
        <Stack display="flex" flexDirection="row" alignItems="center" height="100%">
            <Switch
                color="primary"
                checked={isSubscribedWeekly}
                onClick={(e) => SubscribedWeekly({is_subscribed: e.target.checked, frequency: 'Weekly'})}
            />
        </Stack>
    );
};

const MonthlySubCell = ({isSubscribedMonthly, SubscribedMonthly}) => {
    return (
        <Stack display="flex" flexDirection="row" alignItems="center" height="100%">
            <Switch
                color="primary"
                checked={isSubscribedMonthly}
                onClick={(e) => SubscribedMonthly({is_subscribed: e.target.checked, frequency: 'Monthly'})}
            />
        </Stack>
    );
};

const ActionCell = ({isLoading, handleAction}) => {
    return (
        <Stack display="flex" flexDirection="row" alignItems="center" height="100%">
            <ButtonBase
                disableRipple
                onClick={handleAction}
                sx={{display: 'flex', alignContent: 'center', gap: '6px', opacity: isLoading ? 0.5 : 1}}
                disabled={isLoading}
            >
                <Typography variant="body1" fontWeight={'fontWeightMedium'} sx={{color: '#149CE0'}}>
                    Export
                </Typography>
                {isLoading && <CircularProgress size="14px" sx={{color: '#149CE0'}} />}
            </ButtonBase>
        </Stack>
    );
};

export const ReportsTable = () => {
    const params = useParams();

    const communitySfid = params?.community_sfid;
    const accountSfid = params?.account_sfid;

    const userEmail = useSelector(getEmail);
    const userAccountSfid = useSelector(getAccountSfid);
    const userAccountId = useSelector(getTeamMemberId);
    const userReportSubscriptionList = useSelector(getTeamMemberReportSubscriptionList);

    const [SendAllTimeCashBackTransactionsReport, {isLoading: isSendingAllTimeCashBackTransactionsReport}] =
        useSendAllTimeCashBackTransactionsReportMutation();

    const [SendClaimedCashBackReport, {isLoading: isSendingClaimedCashBackReport}] = useSendClaimedCashBackReportMutation();

    const [SendPropertySummaryReport, {isLoading: isSendingPropertySummaryReport}] = useSendPropertySummaryReportMutation();

    const [SubscribeReport] = useSubscribeReportMutation();

    const getSubscribeReportPayload = useCallback(
        ({report_id, ...payload}) => {
            return {
                user_email: userEmail,
                user_id: userAccountId,
                ...(report_id === 'property_summary_report' ? {account_sfid: userAccountSfid} : {}),
                report_id,
                ...payload
            };
        },
        [userEmail, userAccountSfid, userAccountId]
    );

    const rows = useMemo(
        () => [
            {
                ...AllTimeCashBackTransactionsReportProps,
                isLoading: isSendingAllTimeCashBackTransactionsReport,
                isSubscribedWeekly:
                    userReportSubscriptionList?.find(({report_id}) => report_id === AllTimeCashBackTransactionsReportProps.report_id)
                        ?.is_weekly ?? false,
                isSubscribedMonthly:
                    userReportSubscriptionList?.find(({report_id}) => report_id === AllTimeCashBackTransactionsReportProps.report_id)
                        ?.is_monthly ?? false,
                ...['SubscribedWeekly', 'SubscribedMonthly'].reduce((acc, key) => {
                    acc[key] = (payload) =>
                        SubscribeReport(getSubscribeReportPayload({...AllTimeCashBackTransactionsReportProps, ...payload}));
                    return acc;
                }, {}),
                handleAction: () =>
                    SendAllTimeCashBackTransactionsReport({
                        user_email: userEmail,
                        ...(communitySfid ? {building_sfid: communitySfid} : {account_sfid: accountSfid})
                    })
            },
            {
                ...ClaimedCashBackReportProps,
                isLoading: isSendingClaimedCashBackReport,
                isSubscribedWeekly:
                    userReportSubscriptionList?.find(({report_id}) => report_id === ClaimedCashBackReportProps.report_id)?.is_weekly ??
                    false,
                isSubscribedMonthly:
                    userReportSubscriptionList?.find(({report_id}) => report_id === ClaimedCashBackReportProps.report_id)?.is_monthly ??
                    false,
                ...['SubscribedWeekly', 'SubscribedMonthly'].reduce((acc, key) => {
                    acc[key] = (payload) => SubscribeReport(getSubscribeReportPayload({...ClaimedCashBackReportProps, ...payload}));
                    return acc;
                }, {}),
                handleAction: () =>
                    SendClaimedCashBackReport({
                        user_email: userEmail,
                        ...(communitySfid ? {building_sfid: communitySfid} : {account_sfid: accountSfid})
                    })
            },
            {
                ...ClaimedCashBackSummaryReport,
                isLoading: isSendingPropertySummaryReport,
                isSubscribedWeekly:
                    userReportSubscriptionList?.find(({report_id}) => report_id === ClaimedCashBackSummaryReport.report_id)?.is_weekly ??
                    false,
                isSubscribedMonthly:
                    userReportSubscriptionList?.find(({report_id}) => report_id === ClaimedCashBackSummaryReport.report_id)?.is_monthly ??
                    false,
                ...['SubscribedWeekly', 'SubscribedMonthly'].reduce((acc, key) => {
                    acc[key] = (payload) => SubscribeReport(getSubscribeReportPayload({...ClaimedCashBackSummaryReport, ...payload}));
                    return acc;
                }, {}),
                handleAction: () =>
                    SendPropertySummaryReport({
                        user_email: userEmail,
                        ...(communitySfid ? {building_sfid: communitySfid} : {account_sfid: accountSfid})
                    })
            }
        ],
        [
            isSendingAllTimeCashBackTransactionsReport,
            isSendingClaimedCashBackReport,
            isSendingPropertySummaryReport,
            accountSfid,
            communitySfid,
            userEmail,
            userReportSubscriptionList,
            getSubscribeReportPayload,
            SendAllTimeCashBackTransactionsReport,
            SendClaimedCashBackReport,
            SendPropertySummaryReport,
            SubscribeReport
        ]
    );

    const columns = useMemo(
        () => [
            ...ReportsColumns.map((column) => ({
                ...column,
                field: column.id,
                headerName: column.label,
                renderCell: (params) => <TableBodyCell column={column} params={params} />,
                renderEditCell: (params) => <TableBodyCell column={column} params={params} />,
                valueFormatter: (params) => valueGetter(params, column),
                sortComparator: column.numeric ? (v1, v2) => sortNumbers(v1, v2) : (v1, v2) => sortStrings(v1, v2)
            })),
            {
                field: 'weekly_sub',
                headerName: 'Weekly Subscription (every Monday)',
                renderCell: ({row}) => {
                    return <WeeklySubCell {...row} />;
                },
                flex: 1
            },
            {
                field: 'monthly_sub',
                headerName: 'Monthly Subscription (1st of month)',
                renderCell: ({row}) => {
                    return <MonthlySubCell {...row} />;
                },
                flex: 1
            },
            {
                field: 'action',
                headerName: 'Action',
                renderCell: ({row}) => {
                    return <ActionCell {...row} />;
                },
                flex: 1
            }
        ],
        []
    );

    const mainComponent = useMemo(() => {
        return (
            <Paper sx={{boxShadow: 'none', borderRadius: '8px'}}>
                <Stack direction={'column'} divider={<Divider />}>
                    <Typography variant="h5" fontWeight="fontWeightMedium" sx={{p: '16px'}}>
                        Reports
                    </Typography>
                    <StyledTable
                        columns={columns}
                        rows={rows}
                        pagination
                        pageSizeOptions={[10, 20, 50, 100]}
                        initialState={{pagination: {paginationModel: {pageSize: 10}}}}
                        disableVirtualization={true}
                        slots={{
                            toolbar: () => <ExportToolBarWithSearch />
                        }}
                        density="standard"
                        rowHeight={40}
                        autoHeight
                        getRowId={(row) => row.report_id}
                        disableRowSelectionOnClick
                        sx={{
                            '&.MuiDataGrid-root': {
                                borderRadius: 0,
                                borderColor: 'transparent'
                            },
                            '& .export-button': {display: 'none'}
                        }}
                    />
                </Stack>
            </Paper>
        );
    }, [rows, columns]);

    return mainComponent;
};

WeeklySubCell.propTypes = {
    isSubscribedWeekly: PropTypes.bool,
    SubscribedWeekly: PropTypes.func
};

MonthlySubCell.propTypes = {
    isSubscribedMonthly: PropTypes.bool,
    SubscribedMonthly: PropTypes.func
};

ActionCell.propTypes = {
    isLoading: PropTypes.bool,
    handleAction: PropTypes.func
};
